import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import apiClient from '../../request/http-common';
import { setUser } from './userSlice';
import { setAuthStatus, setToken } from '../../request/Auth/authSlice';
import { Currency } from '@depay/local-currency';
import {
  Box, Typography, FormControl, OutlinedInput, InputLabel,
  InputAdornment, IconButton, Alert, Link, Grid
} from '@mui/material';
import {
  Visibility, VisibilityOff, Email, Lock, Person, Phone,
  LocationOn, ArrowForward, Public, Search, Shield, TrendingUp, Speed
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const AuthInput = styled(OutlinedInput)(({ theme }) => ({
  borderRadius: '10px',
  fontSize: '15px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    borderWidth: '1.5px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600', borderWidth: '2px' },
}));

const SubmitButton = styled('button')(({ disabled }) => ({
  width: '100%',
  height: '52px',
  border: 'none',
  borderRadius: '10px',
  background: disabled ? 'rgba(247,166,0,0.4)' : 'linear-gradient(135deg, #F7A600 0%, #FFB52E 100%)',
  color: '#0a0d1a',
  fontWeight: 700,
  fontSize: '16px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.2s',
  boxShadow: disabled ? 'none' : '0 4px 20px rgba(247,166,0,0.35)',
}));

const features = [
  { icon: <TrendingUp sx={{ fontSize: 20, color: '#F7A600' }} />, text: 'Access 200+ crypto, stocks & forex' },
  { icon: <Shield sx={{ fontSize: 20, color: '#F7A600' }} />, text: 'Regulated & fully insured accounts' },
  { icon: <Speed sx={{ fontSize: 20, color: '#F7A600' }} />, text: 'Start trading in under 3 minutes' },
];

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const appMode = useSelector((state) => state.mode.mode);
  const redirectTo = useSelector((state) => state.redirect.redirect);
  const inviter = useSelector((state) => state.referrals.inviter);

  const [countries, setCountries] = useState('');
  const [error, setErros] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [openCountrySelect, setOpenCountrySelect] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    name: '', email: '', password: '', password_confirmation: '',
    phone: '', address: '', referral: '', country: ''
  });

  const { refetch: getCountries } = useQuery('countries',
    async () => await apiClient.get('/api/countries'),
    {
      enabled: true,
      onSuccess: (res) => { if (res.data.success === true) setCountries(res.data.countries); },
      onError: () => {},
    }
  );

  const onChange = (e) => setRegisterForm({ ...registerForm, [e.target.name]: e.target.value });

  const handleCountrySelect = (countryName) => {
    setRegisterForm({ ...registerForm, country: countryName });
    setCountrySearch('');
    setOpenCountrySelect(false);
  };

  const filteredCountries = countries && countries.length > 0
    ? countries.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()))
    : [];

  let currency = Currency.getCode();

  const { isLoading: isSendingRequest, mutate: postLogin } = useMutation(
    async () => await apiClient.post('/api/register', {
      name: registerForm.name, email: registerForm.email,
      password: registerForm.password, password_confirmation: registerForm.password_confirmation,
      phone: registerForm.phone, address: registerForm.address,
      referral: inviter, country: registerForm.country, currency,
    }),
    {
      onSuccess: (res) => {
        localStorage.setItem('auth-token', res.data.access_token);
        dispatch(setUser(res.data.user));
        dispatch(setAuthStatus('authenticated'));
        dispatch(setToken(res.data.access_token));
        localStorage.setItem('notification', JSON.stringify({ message: `${res.data.message}`, type: 'success' }));
        window.dispatchEvent(new Event('newMessage'));
        window.dispatchEvent(new Event('processed'));
        if (redirectTo !== '') { navigate(redirectTo); } else { window.location.href = '/'; }
      },
      onError: (err) => {
        setErros(err.response?.data?.errors || err.message);
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  const onSubmit = () => {
    window.dispatchEvent(new Event('processing'));
    postLogin();
  };

  useEffect(() => { getCountries(); }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openCountrySelect && !e.target.closest('.country-selector')) {
        setOpenCountrySelect(false);
        setCountrySearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openCountrySelect]);

  const isDark = appMode === 'dark';

  const fieldLabel = (label) => ({
    sx: { '&.Mui-focused': { color: '#F7A600' } }
  });

  const iconColor = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)';

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: isDark ? '#080c18' : '#f1f5f9' }}>

      {/* ── Left Brand Panel ── */}
      <Box sx={{
        width: { xs: 0, lg: '38%' },
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: { lg: 5, xl: 7 },
        background: 'linear-gradient(160deg, #080c18 0%, #0d1526 55%, #111e35 100%)',
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        <Box sx={{ position: 'absolute', top: '-80px', right: '-80px', width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(247,166,0,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: '-60px', left: '-60px', width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(247,166,0,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '32px 32px', pointerEvents: 'none' }} />

        <Box>
          <img src="/intracapital-logos/intracapital-logo-white.png" alt="INTRA CAPITAL" style={{ height: '44px', marginBottom: '48px' }} />

          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ height: '2px', width: '28px', bgcolor: '#F7A600' }} />
            <Typography sx={{ color: '#F7A600', fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              Join 500,000+ Investors
            </Typography>
          </Box>

          <Typography sx={{ fontSize: { lg: '32px', xl: '40px' }, fontWeight: 800, color: '#fff', lineHeight: 1.2, mb: 3 }}>
            Your Journey to{' '}
            <Box component="span" sx={{ background: 'linear-gradient(90deg, #F7A600, #FFD166)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Financial Growth
            </Box>{' '}
            Starts Here.
          </Typography>

          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.7, mb: 5, maxWidth: 340 }}>
            Open your account in minutes and access professional capital markets tools used by institutional investors worldwide.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 6 }}>
            {features.map((f, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(247,166,0,0.1)', border: '1px solid rgba(247,166,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {f.icon}
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '14px', fontWeight: 500 }}>{f.text}</Typography>
              </Box>
            ))}
          </Box>

          {/* Trust badge */}
          <Box sx={{ p: 2.5, borderRadius: '12px', bgcolor: 'rgba(247,166,0,0.06)', border: '1px solid rgba(247,166,0,0.15)' }}>
            <Typography sx={{ color: '#F7A600', fontSize: '12px', fontWeight: 700, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Regulated & Secure
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
              Company No. 14324154 · England & Wales
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px' }}>
          © {new Date().getFullYear()} INTRA CAPITAL LIMITED. All rights reserved.
        </Typography>
      </Box>

      {/* ── Right Form Panel ── */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        p: { xs: 3, sm: 5 },
        bgcolor: isDark ? '#0d1117' : '#ffffff',
        overflowY: 'auto',
        minHeight: '100vh',
      }}>
        <Box sx={{ width: '100%', maxWidth: '680px', py: 4 }}>

          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'block', lg: 'none' }, textAlign: 'center', mb: 5 }}>
            <img src={isDark ? '/intracapital-logos/intracapital-logo-white.png' : '/intracapital-logos/intracapital-logo.png'} alt="INTRA CAPITAL" style={{ height: '44px' }} />
          </Box>

          {/* Heading */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontSize: '28px', fontWeight: 800, color: isDark ? '#fff' : '#0f172a', mb: 1 }}>
              Create your account
            </Typography>
            <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)', fontSize: '15px' }}>
              Already have an account?{' '}
              <Link component={RouterLink} to="/login" underline="none" sx={{ color: '#F7A600', fontWeight: 600 }}>Sign in</Link>
            </Typography>
          </Box>

          {/* Referral info */}
          {inviter !== '' && inviter !== null && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: '10px', fontSize: '14px' }}>
              Referral code applied: <strong>{inviter}</strong>
            </Alert>
          )}

          {/* Error */}
          {error && typeof error === 'string' && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: '10px', fontSize: '14px' }}>{error}</Alert>
          )}

          {/* Form grid */}
          <Grid container spacing={2.5}>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel {...fieldLabel('Full Name')}>Full Name</InputLabel>
                <AuthInput error={!!(error?.name)} onChange={onChange} id="name" name="name" type="text" label="Full Name"
                  startAdornment={<InputAdornment position="start"><Person sx={{ color: iconColor, fontSize: '19px' }} /></InputAdornment>}
                />
                {error?.name && <Typography color="error" sx={{ mt: 0.5, fontSize: '13px' }}>{error.name}</Typography>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel {...fieldLabel('Email Address')}>Email Address</InputLabel>
                <AuthInput error={!!(error?.email)} onChange={onChange} id="email" type="email" name="email" label="Email Address"
                  startAdornment={<InputAdornment position="start"><Email sx={{ color: iconColor, fontSize: '19px' }} /></InputAdornment>}
                />
                {error?.email && <Typography color="error" sx={{ mt: 0.5, fontSize: '13px' }}>{error.email}</Typography>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel {...fieldLabel('Phone Number')}>Phone Number</InputLabel>
                <AuthInput error={!!(error?.phone)} onChange={onChange} id="phone" name="phone" type="tel" label="Phone Number"
                  startAdornment={<InputAdornment position="start"><Phone sx={{ color: iconColor, fontSize: '19px' }} /></InputAdornment>}
                />
                {error?.phone && <Typography color="error" sx={{ mt: 0.5, fontSize: '13px' }}>{error.phone}</Typography>}
              </FormControl>
            </Grid>

            {/* Country selector */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ position: 'relative' }}>
                <Box className="country-selector" sx={{ position: 'relative', zIndex: openCountrySelect ? 10 : 1 }}>
                  <AuthInput
                    value={registerForm.country || ''}
                    onClick={() => setOpenCountrySelect(true)}
                    readOnly
                    placeholder="Select Country"
                    startAdornment={
                      <InputAdornment position="start">
                        {registerForm.country && countries
                          ? <img src={countries.find(c => c.name === registerForm.country)?.flag} alt="flag" style={{ width: '20px', height: '15px', borderRadius: '2px', objectFit: 'cover' }} />
                          : <Public sx={{ color: iconColor, fontSize: '19px' }} />
                        }
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton onClick={() => setOpenCountrySelect(!openCountrySelect)} edge="end" sx={{ color: iconColor }}>
                          <ArrowForward sx={{ fontSize: '18px', transform: openCountrySelect ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {openCountrySelect && (
                    <Box sx={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, mt: 1,
                      bgcolor: isDark ? '#1a2035' : '#fff',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                      borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
                      maxHeight: '280px', overflow: 'hidden',
                    }}>
                      <Box sx={{ p: 1.5, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}` }}>
                        <AuthInput fullWidth placeholder="Search countries…" value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          startAdornment={<InputAdornment position="start"><Search sx={{ color: iconColor, fontSize: '18px' }} /></InputAdornment>}
                          sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                        />
                      </Box>
                      <Box sx={{ maxHeight: '220px', overflowY: 'auto' }}>
                        {filteredCountries.length > 0 ? filteredCountries.map((country) => (
                          <Box key={country.id} onClick={() => handleCountrySelect(country.name)} sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, cursor: 'pointer',
                            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
                            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
                          }}>
                            <img src={country.flag} alt={country.name} style={{ width: '22px', height: '16px', borderRadius: '2px', objectFit: 'cover' }} />
                            <Typography sx={{ fontSize: '14px', color: isDark ? 'rgba(255,255,255,0.8)' : '#0f172a' }}>{country.name}</Typography>
                          </Box>
                        )) : (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', fontSize: '14px' }}>No countries found</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
                {error?.country && <Typography color="error" sx={{ mt: 0.5, fontSize: '13px' }}>{error.country}</Typography>}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel {...fieldLabel('Address')}>Address</InputLabel>
                <AuthInput error={!!(error?.address)} onChange={onChange} id="address" name="address" type="text" label="Address"
                  startAdornment={<InputAdornment position="start"><LocationOn sx={{ color: iconColor, fontSize: '19px' }} /></InputAdornment>}
                />
                {error?.address && <Typography color="error" sx={{ mt: 0.5, fontSize: '13px' }}>{error.address}</Typography>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel {...fieldLabel('Password')}>Password</InputLabel>
                <AuthInput error={!!(error?.password)} onChange={onChange} id="password" name="password"
                  type={showPassword ? 'text' : 'password'} label="Password"
                  startAdornment={<InputAdornment position="start"><Lock sx={{ color: iconColor, fontSize: '19px' }} /></InputAdornment>}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: iconColor }}>
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {error?.password && <Typography color="error" sx={{ mt: 0.5, fontSize: '13px' }}>{error.password}</Typography>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel {...fieldLabel('Confirm Password')}>Confirm Password</InputLabel>
                <AuthInput error={!!(error?.password_confirmation)} onChange={onChange} id="password_confirmation" name="password_confirmation"
                  type={showConfirmPassword ? 'text' : 'password'} label="Confirm Password"
                  startAdornment={<InputAdornment position="start"><Lock sx={{ color: iconColor, fontSize: '19px' }} /></InputAdornment>}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" sx={{ color: iconColor }}>
                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {error?.password_confirmation && <Typography color="error" sx={{ mt: 0.5, fontSize: '13px' }}>{error.password_confirmation}</Typography>}
              </FormControl>
            </Grid>

          </Grid>

          {/* Submit */}
          <Box sx={{ mt: 4 }}>
            <SubmitButton onClick={onSubmit} disabled={isSendingRequest}>
              {isSendingRequest ? 'Creating Account…' : 'Create Account'}
              {!isSendingRequest && <ArrowForward sx={{ fontSize: 18 }} />}
            </SubmitButton>
          </Box>

          <Typography sx={{ textAlign: 'center', mt: 3, fontSize: '13px', color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.35)', lineHeight: 1.6 }}>
            By creating an account you agree to our{' '}
            <Link href="https://intracapitals.com/terms-of-service" target="_blank" rel="noopener noreferrer" underline="none" sx={{ color: '#F7A600', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link href="https://intracapitals.com/privacy-policy" target="_blank" rel="noopener noreferrer" underline="none" sx={{ color: '#F7A600', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
              Privacy Policy
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Register;
